import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook, faChartLine, faCheckCircle, faQuestionCircle,
  faClock, faGraduationCap, faCrown, faArrowRight, faCalendar,
  faTrophy, faAward, faArrowUp, faStar, faChartBar,
  faExclamationTriangle, faCheckCircle as faCheck, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { getExamUrl } from '../utils/slugify';

interface Subscription {
  id: number;
  exam_id: number;
  exam_name: string;
  exam_description: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface Exam {
  id: number;
  name: string;
  description: string;
  badge_image?: string;
  question_count: number;
  category?: string;
}

interface DashboardStats {
  overall: {
    exams_taken: number;
    total_attempts: number;
    average_score: string;
    total_questions_answered: number;
    total_correct_answers: number;
  };
  recentActivity: any[];
  examPerformance: any[];
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [availableExams, setAvailableExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, subscriptionsRes, examsRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get('/subscriptions'),
        api.get('/exams')
      ]);

      setStats(statsRes.data);
      const userSubscriptions = subscriptionsRes.data.subscriptions || [];
      setSubscriptions(userSubscriptions);

      const activeSubscriptionExamIds = new Set(
        userSubscriptions
          .filter((sub: Subscription) => new Date(sub.end_date) > new Date())
          .map((sub: Subscription) => sub.exam_id)
      );

      const allExams = examsRes.data.exams || [];
      const available = allExams.filter((exam: Exam) => !activeSubscriptionExamIds.has(exam.id));
      setAvailableExams(available);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" />
          <p className="gp-text-muted text-sm">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const accuracyRate = stats?.overall.total_questions_answered
    ? ((stats.overall.total_correct_answers / stats.overall.total_questions_answered) * 100).toFixed(1)
    : '0';

  const scoreTone = (score: number) => {
    if (score >= 80) return 'var(--gp-success)';
    if (score >= 60) return 'var(--gp-accent)';
    return '#b91c1c';
  };

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        <div className="gp-page-header">
          <div className="flex items-center gap-3">
            <div className="gp-icon-tile-lg">
              <FontAwesomeIcon icon={faGraduationCap} className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="gp-page-title truncate">
                {t('dashboard.welcomeBack', { name: user?.fullName || user?.email?.split('@')[0] })}
              </h1>
              <p className="gp-page-subtitle">{t('dashboard.continueJourney')}</p>
            </div>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div className="gp-stat text-left">
              <div className="flex items-center justify-between mb-1">
                <FontAwesomeIcon icon={faGraduationCap} className="text-sm" style={{ color: 'var(--gp-brand)' }} />
                <FontAwesomeIcon icon={faArrowUp} className="text-xs gp-text-muted" />
              </div>
              <div className="gp-stat-label">{t('dashboard.examsTaken')}</div>
              <div className="gp-stat-value">{stats.overall.exams_taken || 0}</div>
              <p className="text-[11px] gp-text-muted mt-0.5">{t('dashboard.uniqueCertifications')}</p>
            </div>

            <div className="gp-stat text-left">
              <div className="flex items-center justify-between mb-1">
                <FontAwesomeIcon icon={faCheckCircle} className="text-sm" style={{ color: 'var(--gp-success)' }} />
                <FontAwesomeIcon icon={faCheckCircle} className="text-xs gp-text-muted" />
              </div>
              <div className="gp-stat-label">{t('dashboard.totalAttempts')}</div>
              <div className="gp-stat-value">{stats.overall.total_attempts || 0}</div>
              <p className="text-[11px] gp-text-muted mt-0.5">{t('dashboard.practiceExamSessions')}</p>
            </div>

            <div className="gp-stat text-left">
              <div className="flex items-center justify-between mb-1">
                <FontAwesomeIcon icon={faChartLine} className="text-sm" style={{ color: 'var(--gp-brand)' }} />
                <FontAwesomeIcon icon={faTrophy} className="text-xs" style={{ color: 'var(--gp-accent)' }} />
              </div>
              <div className="gp-stat-label">{t('dashboard.averageScore')}</div>
              <div className="gp-stat-value">
                {stats.overall.average_score ? parseFloat(stats.overall.average_score).toFixed(1) : '0'}%
              </div>
              <p className="text-[11px] gp-text-muted mt-0.5">{t('dashboard.acrossAllAttempts')}</p>
            </div>

            <div className="gp-stat text-left">
              <div className="flex items-center justify-between mb-1">
                <FontAwesomeIcon icon={faQuestionCircle} className="text-sm" style={{ color: 'var(--gp-accent)' }} />
                <FontAwesomeIcon icon={faStar} className="text-xs gp-text-muted" />
              </div>
              <div className="gp-stat-label">{t('dashboard.questionsAnswered')}</div>
              <div className="gp-stat-value">{stats.overall.total_questions_answered || 0}</div>
              <p className="text-[11px] gp-text-muted mt-0.5">{t('dashboard.accuracy', { rate: accuracyRate })}</p>
            </div>
          </div>
        )}

        {stats && stats.overall.total_questions_answered > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="gp-card flex items-center justify-between py-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: 'var(--gp-success)' }}>
                  {t('dashboard.correctAnswers')}
                </p>
                <p className="font-display text-xl font-bold" style={{ color: 'var(--gp-ink)' }}>
                  {stats.overall.total_correct_answers || 0}
                </p>
              </div>
              <FontAwesomeIcon icon={faCheck} style={{ color: 'var(--gp-success)' }} />
            </div>
            <div className="gp-card flex items-center justify-between py-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: '#b91c1c' }}>
                  {t('dashboard.incorrectAnswers')}
                </p>
                <p className="font-display text-xl font-bold" style={{ color: 'var(--gp-ink)' }}>
                  {(stats.overall.total_questions_answered || 0) - (stats.overall.total_correct_answers || 0)}
                </p>
              </div>
              <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#b91c1c' }} />
            </div>
            <div className="gp-card flex items-center justify-between py-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: 'var(--gp-brand)' }}>
                  {t('dashboard.accuracyRate')}
                </p>
                <p className="font-display text-xl font-bold" style={{ color: 'var(--gp-ink)' }}>{accuracyRate}%</p>
              </div>
              <FontAwesomeIcon icon={faChartBar} style={{ color: 'var(--gp-brand)' }} />
            </div>
          </div>
        )}

        <div className="space-y-5">
          {subscriptions.length > 0 && (
            <div className="gp-card">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="gp-icon-tile" style={{ background: 'var(--gp-accent-soft)', color: 'var(--gp-accent)' }}>
                    <FontAwesomeIcon icon={faCrown} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="gp-heading-lg text-lg">{t('dashboard.activeSubscriptions')}</h2>
                    <p className="text-xs gp-text-muted">{t('dashboard.activePremiumAccess')}</p>
                  </div>
                </div>
                <span className="gp-badge shrink-0">
                  {subscriptions.filter(sub => new Date(sub.end_date) > new Date()).length} {t('mySubscriptions.active')}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {subscriptions
                  .filter(sub => new Date(sub.end_date) > new Date())
                  .map((subscription) => {
                    const endDate = new Date(subscription.end_date);
                    const daysRemaining = Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                    const isExpiringSoon = daysRemaining <= 30;
                    const progressPercent = Math.min((daysRemaining / 365) * 100, 100);

                    return (
                      <Link
                        key={subscription.id}
                        to={getExamUrl(subscription.exam_id, subscription.exam_name)}
                        className="gp-card gp-card-interactive block"
                        style={{ background: 'var(--gp-accent-soft)', borderColor: 'var(--gp-accent)' }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <FontAwesomeIcon icon={faCrown} className="text-sm" style={{ color: 'var(--gp-accent)' }} />
                          <h3 className="text-sm font-bold truncate" style={{ color: 'var(--gp-ink)' }}>
                            {subscription.exam_name}
                          </h3>
                        </div>
                        <p className="text-xs gp-text-muted line-clamp-2 mb-3">
                          {subscription.exam_description}
                        </p>

                        <div className="mb-3">
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <span className="gp-text-muted font-medium">{t('dashboard.subscriptionProgress')}</span>
                            <span className="font-semibold" style={{ color: isExpiringSoon ? '#b91c1c' : 'var(--gp-ink)' }}>
                              {t(daysRemaining === 1 ? 'dashboard.daysLeft' : 'dashboard.daysLeftPlural', { count: daysRemaining })}
                            </span>
                          </div>
                          <div className="w-full rounded-full h-1.5" style={{ background: 'var(--gp-surface-muted)' }}>
                            <div
                              className="h-1.5 rounded-full transition-all duration-500"
                              style={{
                                width: `${progressPercent}%`,
                                background: isExpiringSoon ? 'var(--gp-accent)' : 'var(--gp-brand)',
                              }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid var(--gp-border)' }}>
                          <div className="flex items-center gap-2 text-xs gp-text-muted">
                            <FontAwesomeIcon icon={faCalendar} />
                            <span>{t('dashboard.expiresOn', { date: endDate.toLocaleDateString() })}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--gp-accent)' }}>
                            <span>{t('dashboard.viewExam')}</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
              </div>
            </div>
          )}

          {subscriptions.filter(sub => new Date(sub.end_date) > new Date()).length === 0 && availableExams.length > 0 && (
            <div className="gp-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="gp-icon-tile">
                  <FontAwesomeIcon icon={faGraduationCap} />
                </div>
                <div>
                  <h2 className="gp-heading-lg text-lg">{t('dashboard.availableCertifications')}</h2>
                  <p className="text-xs gp-text-muted">{t('dashboard.subscribeToUnlock')}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {availableExams.slice(0, 6).map((exam) => (
                  <Link
                    key={exam.id}
                    to={`/subscription/${exam.id}`}
                    className="gp-card gp-card-interactive block"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {exam.badge_image ? (
                        <>
                          <img
                            src={`/${exam.badge_image}`}
                            alt={exam.name}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              const icon = (e.target as HTMLImageElement).parentElement?.querySelector('.badge-fallback-icon') as HTMLElement;
                              if (icon) icon.classList.remove('hidden');
                            }}
                          />
                          <FontAwesomeIcon
                            icon={faGraduationCap}
                            className="badge-fallback-icon hidden text-lg"
                            style={{ color: 'var(--gp-brand)' }}
                          />
                        </>
                      ) : (
                        <FontAwesomeIcon icon={faGraduationCap} className="text-lg" style={{ color: 'var(--gp-brand)' }} />
                      )}
                      <h3 className="text-sm font-bold line-clamp-2" style={{ color: 'var(--gp-ink)' }}>
                        {exam.name}
                      </h3>
                    </div>
                    <p className="text-xs gp-text-muted line-clamp-2 mb-3">{exam.description}</p>
                    <div className="flex items-center gap-2 text-xs gp-text-muted mb-3">
                      <FontAwesomeIcon icon={faQuestionCircle} className="text-xs" />
                      <span>{exam.question_count || 0} {t('dashboard.questions')}</span>
                    </div>
                    <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid var(--gp-border)' }}>
                      <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: 'var(--gp-ink-muted)' }}>
                        <FontAwesomeIcon icon={faCrown} style={{ color: 'var(--gp-accent)' }} />
                        <span>{t('dashboard.subscribe')}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--gp-brand)' }}>
                        <span>{t('dashboard.viewDetails')}</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              {availableExams.length > 6 && (
                <div className="mt-4 text-center">
                  <Link to="/certifications" className="gp-link inline-flex items-center gap-2 text-sm">
                    <span>{t('dashboard.viewAllCertifications')}</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              )}
            </div>
          )}

          {stats && stats.recentActivity.length > 0 && (
            <div className="gp-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="gp-icon-tile">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div>
                  <h2 className="gp-heading-lg text-lg">{t('dashboard.recentActivity')}</h2>
                  <p className="text-xs gp-text-muted">{t('dashboard.recentActivity')}</p>
                </div>
              </div>
              <div className="space-y-2">
                {stats.recentActivity.slice(0, 5).map((activity: any) => {
                  const scorePercent = parseFloat(activity.score_percent);
                  const getScoreBadge = () => {
                    if (scorePercent >= 90) return { icon: faTrophy, text: t('dashboard.excellent'), color: 'var(--gp-accent)' };
                    if (scorePercent >= 80) return { icon: faAward, text: t('dashboard.great'), color: 'var(--gp-success)' };
                    if (scorePercent >= 60) return { icon: faChartLine, text: t('dashboard.good'), color: 'var(--gp-brand)' };
                    return { icon: faArrowUp, text: t('dashboard.practice'), color: 'var(--gp-accent)' };
                  };
                  const badge = getScoreBadge();
                  const completedDate = new Date(activity.completed_at);
                  const isToday = completedDate.toDateString() === new Date().toDateString();
                  const isYesterday = completedDate.toDateString() === new Date(Date.now() - 86400000).toDateString();

                  return (
                    <Link
                      key={activity.id}
                      to={`/exam/${activity.exam_id}`}
                      className="gp-card gp-card-interactive flex items-center justify-between gap-3 py-3"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 text-white"
                          style={{ background: 'var(--gp-brand)' }}
                        >
                          <FontAwesomeIcon icon={badge.icon} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-semibold text-sm truncate" style={{ color: 'var(--gp-ink)' }}>
                              {activity.exam_name}
                            </p>
                            <span className="text-[11px] font-semibold shrink-0" style={{ color: badge.color }}>
                              {badge.text}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs gp-text-muted">
                            <span className="flex items-center gap-1 uppercase">
                              <FontAwesomeIcon icon={activity.mode === 'EXAM' ? faGraduationCap : faBook} className="text-xs" />
                              {activity.mode === 'EXAM' ? t('exam.examMode') : t('exam.practiceMode')}
                            </span>
                            <span>·</span>
                            <span>
                              {activity.correct_answers}/{activity.total_questions} {t('dashboard.correct')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display text-lg font-bold" style={{ color: scoreTone(scorePercent) }}>
                          {scorePercent.toFixed(1)}%
                        </div>
                        <p className="text-xs gp-text-muted">
                          {isToday ? t('dashboard.today') : isYesterday ? t('dashboard.yesterday') : completedDate.toLocaleDateString()}
                        </p>
                        <p className="text-[11px] gp-text-muted mt-0.5">
                          {completedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {stats.recentActivity.length > 5 && (
                <div className="mt-3 text-center">
                  <Link to="/dashboard" className="gp-link text-sm">
                    {t('dashboard.viewAll')} →
                  </Link>
                </div>
              )}
            </div>
          )}

          {stats && stats.examPerformance && stats.examPerformance.length > 0 && (
            <div className="gp-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="gp-icon-tile">
                  <FontAwesomeIcon icon={faChartBar} />
                </div>
                <div>
                  <h2 className="gp-heading-lg text-lg">{t('dashboard.examPerformance')}</h2>
                  <p className="text-xs gp-text-muted">{t('dashboard.yourPerformanceByCert')}</p>
                </div>
              </div>
              <div className="space-y-2">
                {stats.examPerformance.slice(0, 5).map((exam: any) => {
                  const avgScore = parseFloat(exam.avg_score) || 0;
                  const bestScore = parseFloat(exam.best_score) || 0;

                  return (
                    <Link
                      key={exam.id}
                      to={getExamUrl(exam.id, exam.name)}
                      className="gp-card gp-card-interactive flex items-center justify-between gap-3 py-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <h3 className="font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>
                            {exam.name}
                          </h3>
                          <span className="gp-badge-brand text-[11px] font-bold px-2 py-0.5 rounded">
                            {exam.attempts} {exam.attempts === 1 ? t('dashboard.attempt') : t('dashboard.attempts')}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs flex-wrap">
                          <div>
                            <span className="gp-text-muted">{t('dashboard.avg')} </span>
                            <span className="font-semibold" style={{ color: scoreTone(avgScore) }}>
                              {avgScore.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="gp-text-muted">{t('dashboard.best')} </span>
                            <span className="font-semibold" style={{ color: scoreTone(bestScore) }}>
                              {bestScore.toFixed(1)}%
                            </span>
                          </div>
                          {exam.last_attempt && (
                            <div className="gp-text-muted">
                              {t('dashboard.last')} {new Date(exam.last_attempt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="gp-text-muted shrink-0"
                      />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {(!stats || stats.overall.total_attempts === 0) && (
            <div className="gp-card text-center py-10">
              <div className="gp-icon-tile-lg mx-auto mb-4">
                <FontAwesomeIcon icon={faGraduationCap} className="text-xl" />
              </div>
              <h3 className="gp-heading-lg mb-2">{t('dashboard.startYourJourney')}</h3>
              <p className="text-sm gp-text-muted mb-5 max-w-md mx-auto">
                {t('dashboard.noActivityMessage')}
              </p>
              <Link to="/certifications" className="gp-btn-primary inline-flex">
                <span>{t('dashboard.browseCertifications')}</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
